import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Button,
  FormControl,
  Autocomplete,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ClassIcon from '@mui/icons-material/Class';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { API_BASE_URL } from 'src/apiConfig';
import { useTranslation } from 'react-i18next';


const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '20px',
    padding: 0,
    width: '460px',
    overflow: 'hidden',
    boxShadow: '0 8px 40px rgba(21,101,192,0.18)',
  },
});

const DialogHeader = styled(Box)({
  background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 60%, #42a5f5 100%)',
  padding: '20px 24px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  color: '#fff',
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#f8faff',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#1976d2' },
});

const UploadButton = styled(Button)({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '14px',
  padding: '8px 16px',
  backgroundColor: '#e3f2fd',
  color: '#1565c0',
  border: '1px dashed #90caf9',
  '&:hover': {
    backgroundColor: '#bbdefb',
    borderColor: '#1976d2',
  },
});

const SaveButton = styled(Button)({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '14px',
  padding: '9px 24px',
  background: 'linear-gradient(135deg, #1565c0, #1976d2)',
  color: '#fff',
  boxShadow: '0 2px 8px rgba(21,101,192,0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #0d47a1, #1565c0)',
    boxShadow: '0 4px 14px rgba(21,101,192,0.4)',
  },
  '&.Mui-disabled': {
    background: '#e0e0e0',
    color: '#bbb',
    boxShadow: 'none',
  },
});

const CancelButton = styled(Button)({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '14px',
  padding: '9px 20px',
  color: '#555',
  backgroundColor: '#f5f5f5',
  '&:hover': { backgroundColor: '#e8e8e8' },
});


const AddDiplomaTemplateDialog = ({
  open,
  onClose,
  onSave,
  template,
  setTemplate,
  diplomaTypes,
}) => {
  const { t } = useTranslation();
  const isEditing = Boolean(template?.id);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) setErrors({});
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTemplate({ ...template, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: '' });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTemplate({ ...template, image: file });
      if (errors.image) setErrors({ ...errors, image: '' });
    }
  };

  const handleSave = () => {
    const validationErrors = {};
    if (!template.name?.trim()) validationErrors.name = t('diplomaTemplate.nameRequired');
    if (!template.type) validationErrors.type = t('diplomaTemplate.typeRequired');
    if (!template.year) validationErrors.year = t('diplomaTemplate.yearRequired');
    if (!template.image) validationErrors.image = t('diplomaTemplate.imageRequired');

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    onSave();
  };

  const previewSrc = template.image instanceof File
    ? URL.createObjectURL(template.image)
    : template.image
      ? `${API_BASE_URL}/api/${template.image}`
      : null;

  return (
    <StyledDialog open={open} onClose={onClose}>
      <DialogHeader>
        <ClassIcon sx={{ fontSize: 28 }} />
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            {isEditing ? t('diplomaTemplate.editTitle') : t('diplomaTemplate.addTitle')}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {isEditing ? t('dialog.edit') : t('diplomaTemplate.addNew')}
          </Typography>
        </Box>
      </DialogHeader>

      <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <StyledTextField
            autoFocus
            label={t('diplomaTemplate.nameLabel')}
            name="name"
            type="text"
            fullWidth
            size="small"
            value={template.name || ''}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
          />

          <FormControl fullWidth>
            <Autocomplete
              size="small"
              options={[2026, 2027, 2028, 2029, 2030, 2031, 2032, 2033, 2034, 2035]}
              value={template.year ? Number(template.year) : null}
              onChange={(_, newValue) => {
                setTemplate({ ...template, year: newValue || '' });
                if (errors.year) setErrors({ ...errors, year: '' });
              }}
              getOptionLabel={(option) => option.toString()}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  label={t('diplomaTemplate.year')}
                  error={!!errors.year}
                  helperText={errors.year}
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth>
            <Autocomplete
              size="small"
              options={diplomaTypes}
              getOptionLabel={(option) => option.label}
              value={diplomaTypes.find((type) => String(type.id) === String(template.type)) || null}
              onChange={(_, newValue) => {
                setTemplate({ ...template, type: newValue ? newValue.id : '' });
                if (errors.type) setErrors({ ...errors, type: '' });
              }}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  label={t('diplomaTemplate.type')}
                  error={!!errors.type}
                  helperText={errors.type}
                />
              )}
            />
          </FormControl>

          <Divider sx={{ borderColor: '#f0f4fb' }} />

          <Box>
            <UploadButton
              component="label"
              startIcon={<UploadFileIcon />}
              fullWidth
            >
              {template.image ? t('diplomaTemplate.uploadImage') + ' ✓' : t('diplomaTemplate.uploadImage')}
              <input type="file" accept="image/*" hidden onChange={handleFileChange} />
            </UploadButton>

            {errors.image && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {errors.image}
              </Typography>
            )}

            {previewSrc && (
              <Box sx={{
                mt: 1.5,
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid #e8edf5',
                backgroundColor: '#f8faff',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                maxHeight: '160px',
              }}>
                <img
                  src={previewSrc}
                  alt="Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '160px',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid #f0f4fb' }}>
        <CancelButton onClick={onClose}>
          {t('dialog.cancel')}
        </CancelButton>
        <SaveButton onClick={handleSave}>
          {t('dialog.save')}
        </SaveButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default AddDiplomaTemplateDialog;